import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Reply from '@mui/icons-material/Reply';
import axios from '../axiosConfig';
import { completeImageUrl } from '../utils/imageUrl';
import HeartIcon from '../components/HeartIcon';
import ProductInfoContent from '../components/ProductInfoContent';
import '../components/ProductsList.css';
import '../components/AuthPopup.css';
import './ProfilePage.css';
import { getProductImageUrl } from '../utils/productImage';
import FeedCaptionMeta from '../components/FeedCaptionMeta';


export default function ProductDetailPage({ onOpenAuth }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [likedIds, setLikedIds] = useState([]);
  const [cartIds, setCartIds] = useState([]);
  const [flippedProductId, setFlippedProductId] = useState(null);
  const [showBuyDialog, setShowBuyDialog] = useState(false);
  const [buyDialogProduct, setBuyDialogProduct] = useState(null);
  const [buyDialogQuantity, setBuyDialogQuantity] = useState(1);
  const [toasts, setToasts] = useState([]);
  const [clickTimeouts, setClickTimeouts] = useState({});
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [profileImageError, setProfileImageError] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [accountTitle, setAccountTitle] = useState('');
  const [currentUserAccountId, setCurrentUserAccountId] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Reset all product state when ID changes
  useEffect(() => {
    setProduct(null);
    setLoading(true);
    setFlippedProductId(null);
    setShowBuyDialog(false);
    setBuyDialogProduct(null);
    setBuyDialogQuantity(1);
    setToasts([]);
    setClickTimeouts({});
    setProfileImageUrl('');
    setProfileImageError(false);
    setAccountName('');
    setAccountTitle('');
  }, [id]);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`/api/v1/products/${id}`);
        const fetchedProduct = response.data;
        setProduct(fetchedProduct);
        
        // Set liked and cart state
        if (localStorage.getItem('authToken')) {
          // Fetch current user account_id
          try {
            const userResponse = await axios.get('/api/v1/users/current');
            if (userResponse.data.account_id) {
              setCurrentUserAccountId(userResponse.data.account_id);
            }
          } catch (error) {
            console.error('Error fetching user account_id:', error);
          }
          
          if (fetchedProduct.liked_by_current_user) {
            setLikedIds([fetchedProduct.id]);
          }
          if (fetchedProduct.in_cart) {
            setCartIds([fetchedProduct.id]);
          }
        }
        
        // Set profile image from account_url if available
        if (fetchedProduct.account_url) {
          setProfileImageUrl(completeImageUrl(fetchedProduct.account_url));
          setProfileImageError(false);
        }
        
        // Set account name from product
        let currentAccountName = '';
        if (fetchedProduct.account_name) {
          currentAccountName = fetchedProduct.account_name;
          setAccountName(currentAccountName);
        }
        // Display name of the shop ("Tea Room"), straight from the product
        if (fetchedProduct.account_title) {
          setAccountTitle(fetchedProduct.account_title);
        }
        
        // Fetch account details if account_id is available
        if (fetchedProduct.account_id) {
          try {
            const accountResponse = await axios.get(`/api/v1/accounts/${fetchedProduct.account_id}`);
            if (accountResponse.data) {
              if (accountResponse.data.title) {
                setAccountTitle(accountResponse.data.title);
              }
              if (accountResponse.data.name && !currentAccountName) {
                setAccountName(accountResponse.data.name);
              }
              if (accountResponse.data.profile_image_url && !fetchedProduct.account_url) {
                setProfileImageUrl(completeImageUrl(accountResponse.data.profile_image_url));
              }
            }
          } catch (error) {
            console.error('Error fetching account details:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const openInfo = (product, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFlippedProductId(product.id);
  };

  const closeInfo = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFlippedProductId(null);
  };

  const toggleLike = (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: `/products/${id}` } })
      );
      return;
    }
    axios
      .post(`/api/v1/products/${id}/toggle_like`)
      .then((response) => {
        setLikedIds((prev) => {
          if (response.data.liked) {
            return [...prev, id];
          } else {
            return prev.filter((x) => x !== id);
          }
        });
      })
      .catch((error) => console.error('Error toggling like:', error));
  };

  const toggleCart = (product, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: `/products/${id}` } })
      );
      return;
    }
    // Show buy dialog instead of directly adding to cart
    setBuyDialogProduct(product);
    setBuyDialogQuantity(1);
    setShowBuyDialog(true);
  };

  const handleShare = async (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    const productUrl = `${window.location.origin}/products/${id}`;
    
    try {
      // Try to use the Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(productUrl);
        addToast('Посилання скопійовано!');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = productUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        addToast('Посилання скопійовано!');
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      addToast('Помилка копіювання');
    }
  };

  const addToast = (message) => {
    const toastId = Date.now() + Math.random();
    const newToast = {
      id: toastId,
      message: message,
      hiding: false
    };
    
    setToasts(prev => [...prev, newToast]);
    
    // Hide toast after 2.5 seconds
    setTimeout(() => {
      setToasts(prev => prev.map(toast => 
        toast.id === toastId ? { ...toast, hiding: true } : toast
      ));
      
      // Remove toast after animation
      setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== toastId));
      }, 300);
    }, 2500);
  };

  const handleBuyDialogConfirm = () => {
    if (!buyDialogProduct || !buyDialogProduct.product_variations || buyDialogProduct.product_variations.length === 0) {
      return;
    }
    
    // Check if user is trying to buy from themselves
    if (buyDialogProduct.account_id && currentUserAccountId && 
        String(buyDialogProduct.account_id) === String(currentUserAccountId)) {
      addToast('Покупка у себе не дозволена правилами');
      return;
    }
    
    const quantity = buyDialogQuantity || 0;
    if (quantity <= 0) {
      addToast('Введіть кількість');
      return;
    }
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: `/products/${id}` } })
      );
      setShowBuyDialog(false);
      return;
    }

    const variation = buyDialogProduct.product_variations.find(v => v.main) || buyDialogProduct.product_variations[0];
    if (!variation) {
      addToast('Помилка: варіанти товару недоступні');
      return;
    }
    
    // Quantity is number of portions (each portion is 10g)
    const itemsToAdd = quantity;
    
    // Add to cart with quantity - make multiple API calls
    const addToCartPromises = Array.from({ length: itemsToAdd }, () =>
      axios.post(`/api/v1/cart/add_to_cart`, { product_variation_id: variation.id })
    );
    
    Promise.all(addToCartPromises)
      .then(() => {
        setCartIds((prev) => {
          if (!prev.includes(buyDialogProduct.id)) {
            return [...prev, buyDialogProduct.id];
          }
          return prev;
        });
        setShowBuyDialog(false);
        addToast(`Додано ${quantity} порцій (${quantity * 10}g) в кошик!`);
        window.dispatchEvent(new CustomEvent('cartUpdated'));
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        addToast('Помилка додавання в кошик');
        setShowBuyDialog(false);
      });
  };

  const handleMobileCardClick = (productId, e) => {
    // Detect mobile device
    const isMobile = window.innerWidth <= 768;
    
    if (!isMobile) {
      // Desktop behavior - do nothing
      return;
    }
    
    // Mobile behavior - prevent navigation and handle double-click for like
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    const lastClick = clickTimeouts[productId];
    
    if (lastClick && now - lastClick < 300) {
      // Double click detected - toggle like
      clearTimeout(clickTimeouts[`timeout_${productId}`]);
      toggleLike(productId, e);
      setClickTimeouts(prev => {
        const { [productId]: removed, [`timeout_${productId}`]: removedTimeout, ...rest } = prev;
        return rest;
      });
    } else {
      // Single click - do nothing, just update timestamp
      setClickTimeouts(prev => ({
        ...prev,
        [productId]: now
      }));
      
      // Set timeout to clear the click timestamp
      const timeoutId = setTimeout(() => {
        setClickTimeouts(prev => {
          const { [productId]: removed, ...rest } = prev;
          return rest;
        });
      }, 300);
      
      setClickTimeouts(prev => ({
        ...prev,
        [`timeout_${productId}`]: timeoutId
      }));
    }
  };

  const handleProfileClick = () => {
    if (accountName) {
      navigate(`/${accountName}`);
    } else if (product?.account_id) {
      navigate(`/account/${product.account_id}`);
    } else {
      navigate('/');
    }
  };

  // Handle swipe back gesture on mobile
  const minSwipeDistance = 50;
  const swipeEdgeThreshold = 30; // Start swipe from left edge (30px)

  const onTouchStart = (e) => {
    const touch = e.touches[0];
    // Only detect swipe if starting from left edge
    if (touch.clientX <= swipeEdgeThreshold) {
      setTouchEnd(null);
      setTouchStart(touch.clientX);
    }
  };

  const onTouchMove = (e) => {
    if (touchStart !== null && touchStart <= swipeEdgeThreshold) {
      const currentX = e.touches[0].clientX;
      setTouchEnd(currentX);
      
      // If swiping right from edge, prevent default browser back gesture
      if (currentX > touchStart) {
        e.preventDefault();
      }
    }
  };

  const onTouchEnd = (e) => {
    if (!touchStart || !touchEnd) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    const distance = touchEnd - touchStart;
    const isRightSwipe = distance > minSwipeDistance;
    
    // Swipe right (from left edge) to go back
    if (isRightSwipe && touchStart <= swipeEdgeThreshold) {
      // Prevent browser's native back gesture
      e.preventDefault();
      handleProfileClick();
    }
    
    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };

  if (loading) {
    return (
      <div className="products__page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#fff' }}>
        <div className="loading-indicator">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="products__page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#fff', color: '#1f1f1f' }}>
        <div>Товар не знайдено</div>
      </div>
    );
  }

  const imageUrl = getProductImageUrl(product);
  const isLiked = likedIds.includes(product.id);
  const isFlipped = flippedProductId === product.id;

  return (
    <div 
      className="products__page profile-page--list-mode" 
      style={{ padding: 0, paddingBottom: 0, display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden', background: '#fff' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="profile-list-header">
        <div
          className="profile-list-header-info profile-list-header-info--link"
          onClick={handleProfileClick}
          role="link"
          aria-label="Відкрити магазин"
        >
          {profileImageUrl && !profileImageError ? (
            <span className="profile-list-header-avatar">
              <img
              src={profileImageUrl} 
              alt={accountTitle || accountName}
              className="profile-list-header-avatar__img"
              onError={() => setProfileImageError(true)}
            />
            </span>
          ) : (
            <span className="profile-list-header-image profile-list-header-image--placeholder" title={accountTitle || accountName}>
                <span className="profile-list-header-image__name">{(accountTitle || accountName).replace(/^@/, '')}</span>
              </span>
          )}
          <div className="profile-list-header-title-wrapper">
            {(accountTitle || accountName) && (
              <span className="profile-list-header-title">{accountTitle || accountName.replace(/^@/, '')}</span>
            )}
          </div>
        </div>
      </div>
      <div className="products__grid">
        <div 
          className="products__link"
          onClick={(e) => handleMobileCardClick(product.id, e)}
        >
          <div className={`products__item ${isFlipped ? 'products__item--flipped' : ''}`}>
            <div
              className="products__item-background"
              style={{ backgroundImage: `url(${imageUrl})` }}
            />
            <div
              className="products__item-foreground"
              style={{ backgroundImage: `url(${imageUrl})` }}
            />
            {/* dark fade under the caption, same as the home feed */}
            <div className="feed-caption-scrim" aria-hidden="true" />
            {/* Profile button */}
            <div className="products__corner products__corner-topright products__corner-topright-new">
              <button
                className="products__new-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleProfileClick();
                }}
                aria-label="Go to profile"
                style={product.account_url ? {
                  backgroundImage: `url(${completeImageUrl(product.account_url)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                } : {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.4)',
                  backdropFilter: 'blur(4px)'
                }}
              >
                {!product.account_url && (
                  <span className="products__new-toggle-name">{product.account_title || (product.account_name || '').replace(/^@/, '') || 'Магазин'}</span>
                )}
              </button>
            </div>
            {/* Info button */}
            <div className="products__corner products__corner-topright products__corner-topright-info">
              <button
                className="products__info-toggle"
                onClick={(e) => isFlipped ? closeInfo(e) : openInfo(product, e)}
                aria-label={isFlipped ? "Close info" : "Product info"}
              >
                {isFlipped ? '×' : <VisibilityOutlined className="products__info-icon-svg" />}
              </button>
            </div>
            {/* Like button */}
            <div className="products__corner products__corner-topright">
              <button
                className={`products__toggle ${isLiked ? 'liked' : 'unliked'}`}
                onClick={(e) => toggleLike(product.id, e)}
              >
                <HeartIcon />
              </button>
            </div>
            {/* Share button */}
            <div className="products__corner products__corner-middle-right">
              <button
                className="products__share-toggle"
                onClick={(e) => handleShare(e)}
                aria-label="Share product"
              >
                <Reply className="products__share-icon" style={{ transform: 'scaleX(-1)' }} />
              </button>
            </div>
            {/* Cart button */}
            <div className="products__corner products__corner-bottomright">
              <button
                className={`products__cart-toggle ${cartIds.includes(product.id) ? 'inCart' : 'outCart'}`}
                onClick={(e) => toggleCart(product, e)}
              >
                <ShoppingCartIcon className="products__cart-icon" />
              </button>
            </div>
            {/* Product name and price caption - same markup as the home feed (ProductsList) */}
            {product.product_variations && product.product_variations.length > 0 && (
              <div className="feed-caption">
                <span className="feed-caption__name">
                  {product.title}
                </span>
                <FeedCaptionMeta variation={product.product_variations.find(v => v.main) || product.product_variations[0]} />
              </div>
            )}
            {/* Info card back */}
            <div className="products__item-back">
              <ProductInfoContent product={product} />
            </div>
          </div>
        </div>
      </div>
      
      {/* Buy Dialog */}
      {showBuyDialog && buyDialogProduct && (
        <div className="auth-popup-overlay" onClick={() => setShowBuyDialog(false)}>
          <div className="auth-popup-content" onClick={(e) => e.stopPropagation()} style={{ padding: 0 }}>
            <div style={{ textAlign: 'center', paddingTop: '50px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '20px' }}>
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) - 1;
                      setBuyDialogQuantity(Math.max(0, newQty));
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={buyDialogQuantity || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                      setBuyDialogQuantity(Math.max(0, val));
                    }}
                    style={{
                      width: '60px',
                      height: '40px',
                      border: '2px solid #000',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      padding: '0 8px'
                    }}
                  />
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) + 1;
                      setBuyDialogQuantity(newQty);
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    +
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const quantity = buyDialogQuantity || 0;
                      const grams = quantity * 10;
                      return grams > 0 ? `${grams}G` : '0';
                    })()}
                  </div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 'bold', 
                    color: 'var(--color-accent)',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const variation = buyDialogProduct.product_variations.find(v => v.main) || buyDialogProduct.product_variations[0];
                      if (!variation) return '0';
                      const basePrice = parseFloat(variation.price) || 0;
                      const quantity = buyDialogQuantity || 0;
                      // Quantity is number of portions (each portion is 10g)
                      const totalPrice = quantity * basePrice;
                      return totalPrice % 1 === 0 ? totalPrice.toString() : totalPrice.toFixed(2);
                    })()} ₴
                  </div>
                </div>
                {buyDialogProduct && buyDialogProduct.account_id && currentUserAccountId && 
                 String(buyDialogProduct.account_id) === String(currentUserAccountId) && (
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--color-accent)',
                    marginTop: '16px',
                    textAlign: 'center',
                    padding: '8px 16px',
                    backgroundColor: '#fff5f8',
                    borderRadius: '8px'
                  }}>
                    Покупка у себе не дозволена правилами
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleBuyDialogConfirm();
                  }}
                  disabled={(buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? '#ccc' : 'var(--color-accent)',
                    border: 'none',
                    cursor: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'none' : '0 4px 12px rgba(31, 31, 31, 0.3)',
                    transition: 'all 0.3s ease',
                    marginRight: '20px',
                    marginBottom: '20px',
                    opacity: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent-hover)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <ShoppingCartIcon style={{ color: '#fff', fontSize: '28px' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Toasts */}
      {toasts.map((toast, index) => (
        <div 
          key={toast.id}
          className={`toast-notification ${toast.hiding ? 'toast-hiding' : ''}`}
          style={{ top: `${20 + index * 70}px` }}
        >
          <span>{toast.message}</span>
        </div>
      ))}
    </div>
  );
}